import { ReactNode, useMemo, useRef, useState, useEffect } from 'react';
import {
  Search,
  Home,
  CalendarDays,
  Bell,
  BellDot,
  CheckCheck,
  Circle,
  Grid,
  User,
  Building2,
  Moon,
  Sun,
  LogOut,
  Users,
  Coins,
  Gift,
  BarChart3,
  Settings,
  Boxes,
  LayoutDashboard,
  MessageCircle,
  Inbox,
  Stethoscope,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppointmentModalStore } from '../stores/appointmentModalStore';
import { NotificationItem, useNotificationsStore } from '../stores/notificationsStore';
import { useI18n } from '../i18n/LanguageContext';
import { useSentinel } from '../features/sentinel/SentinelProvider';
import { useMessageUnread } from '../hooks/useMessageUnread';
import { API_BASE } from '../config/env';

type Props = {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
};

type HelpSectionItem = {
  title: string;
  desc: string;
  icon: LucideIcon;
  href?: string;
};

type HelpSection = {
  title: string;
  items: HelpSectionItem[];
};

type PatientResult = {
  id: string;
  name: string;
  mrn: string;
  dob: string; // ISO date string
};

type ConnectedAccount = {
  accessToken: string;
  refreshToken: string;
  user: {
    email?: string;
    name?: string;
  } | null;
  expiresAt: number;
  remember: boolean;
};

const AUTH_STORAGE_KEY = 'neoemr.staff.auth';
const CONNECTED_ACCOUNTS_KEY = 'neoemr.staff.connectedAccounts';

type AppointmentResult = {
  id: string;
  patientName: string;
  startTime: string; // ISO date string
  title?: string;
  providerName: string;
};

type EncounterResult = {
  id: string;
  patientName: string;
  date: string; // ISO date string
  status: string;
};

type SearchResult = {
  patients: PatientResult[];
  appointments: AppointmentResult[];
  encounters: EncounterResult[];
};

export default function TopBar({ theme, onToggleTheme }: Props) {
  const { logout, user, getAccessTokenSilently } = useAuth0();
  const { openSentinel } = useSentinel();
  const navigate = useNavigate();
  const { openModal } = useAppointmentModalStore();
  const { t, tr, language } = useI18n();
  const [showMenu, setShowMenu] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const helpRef = useRef<HTMLDivElement | null>(null);
  const notificationsRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const notifications = useNotificationsStore((state) => state.items);
  const hydrated = useNotificationsStore((state) => state.hydrated);
  const hydrateNotifications = useNotificationsStore((state) => state.hydrate);
  const markRead = useNotificationsStore((state) => state.markRead);
  const markAllRead = useNotificationsStore((state) => state.markAllRead);
  const clearAll = useNotificationsStore((state) => state.clearAll);
  const addMockEvent = useNotificationsStore((state) => state.addMockEvent);

  const unreadCount = useMemo(() => notifications.filter((item) => !item.read).length, [notifications]);
  const { count: inboxUnread } = useMessageUnread(30000);

  useEffect(() => {
    if (!showMenu) return;
    try {
      const raw = window.localStorage.getItem(CONNECTED_ACCOUNTS_KEY);
      const rows = raw ? JSON.parse(raw) as ConnectedAccount[] : [];
      setConnectedAccounts(rows.filter((row) => row?.accessToken && row.user?.email));
    } catch {
      setConnectedAccounts([]);
    }
  }, [showMenu]);

  function switchAccount(account: ConnectedAccount) {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(account));
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
    window.location.assign('/appointments');
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setResults(null);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!hydrated) {
      hydrateNotifications();
    }
  }, [hydrated, hydrateNotifications]);

  useEffect(() => {
    if (!hydrated) return;
    const interval = window.setInterval(() => {
      addMockEvent();
    }, 45000);
    return () => window.clearInterval(interval);
  }, [addMockEvent, hydrated]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.length < 3) {
        setResults(null);
        return;
      }

      setLoading(true);
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
        });

        const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setResults(data);
        }
      } catch (error) {
        console.error('Search failed', error);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [query, getAccessTokenSilently]);

  const helpSections = useMemo(
    (): HelpSection[] => [
      {
        title: tr('Quick Access'),
        items: [
          {
            title: tr('Home'),
            desc: tr('Daily clinic snapshot and manager overview.'),
            icon: Home,
            href: '/dashboard',
          },
          {
            title: tr('Appointments'),
            desc: tr('Book, update, and complete patient appointments.'),
            icon: CalendarDays,
            href: '/appointments',
          },
          {
            title: tr('Patients'),
            desc: tr('Open patient charts and review service history.'),
            icon: Users,
            href: '/patients',
          },
          {
            title: tr('Billing'),
            desc: tr('Register payment status and review billing context.'),
            icon: Coins,
            href: '/billing',
          },
        ],
      },
      {
        title: tr('Business + Setup'),
        items: [
          {
            title: tr('Offers & Packages'),
            desc: tr('Manage offers, packages, and prepaid service credits.'),
            icon: Gift,
            href: '/offers',
          },
          {
            title: tr('Reports'),
            desc: tr('Review appointments, top services, and revenue trends.'),
            icon: BarChart3,
            href: '/reports/operations',
          },
          {
            title: tr('Inventory'),
            desc: tr('Receive stock, count inventory, and review low-stock items.'),
            icon: Boxes,
            href: '/inventory',
          },
          {
            title: tr('Settings'),
            desc: tr('Configure locations, services, users, and clinic setup.'),
            icon: Settings,
            href: '/settings',
          },
        ],
      },
    ],
    [tr],
  );

  return (
    <header className="h-16 bg-white border-b border-slate-100 flex items-center px-4 lg:px-8 gap-4">
      <div className="flex-1 min-w-0">
        <div className="relative w-full" ref={searchRef}>
          <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
          <input
            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10"
            placeholder={t('topbar.searchPlaceholder', 'Search patients, appointments, notes...')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {(results || loading) && query.length >= 3 && (
            <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-slate-100 max-h-[80vh] overflow-y-auto z-50 p-2">
              {loading ? (
                <div className="p-4 text-center text-slate-500 text-sm">Searching...</div>
              ) : (
                <div className="space-y-4">
                  {!results?.patients.length && !results?.appointments.length && !results?.encounters.length && (
                    <div className="p-4 text-center text-slate-500 text-sm">No results found.</div>
                  )}

                  {results?.patients && results.patients.length > 0 && (
                    <div>
                      <h4 className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Patients</h4>
                      {results.patients.map((p) => (
                        <div
                          key={p.id}
                          className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition"
                          onClick={() => {
                            setQuery('');
                            setResults(null);
                            navigate(`/patients/${p.id}`);
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center">
                            <User size={14} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-ink">{p.name}</p>
                            <p className="text-xs text-slate-500">
                              MRN: {p.mrn} • DOB: {new Date(p.dob).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {results?.appointments && results.appointments.length > 0 && (
                    <div>
                      <h4 className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Appointments</h4>
                      {results.appointments.map((a) => (
                        <div
                          key={a.id}
                          className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition"
                          onClick={() => {
                            setQuery('');
                            setResults(null);
                            const raw = a as any;
                            const date = new Date(raw.startTime);
                            const localInput = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                            openModal({
                              id: raw.id,
                              patientId: raw.patientId,
                              providerId: raw.providerId,
                              status: raw.status,
                              appointmentTypeId: raw.appointmentTypeId,
                              start: localInput,
                              durationMinutes: raw.appointmentType?.durationMinutes || 30,
                              title: raw.title,
                              isTelemed: raw.isTelemed,
                              generateTelemedLink: false,
                              joinUrl: raw.joinUrl,
                            });
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                            <Search size={14} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-ink">{a.patientName}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(a.startTime).toLocaleString()} • {a.title || 'Visit'} with {a.providerName}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {results?.encounters && results.encounters.length > 0 && (
                    <div>
                      <h4 className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Clinical Notes</h4>
                      {results.encounters.map((e) => (
                        <div key={e.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition">
                          <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
                            <Stethoscope size={14} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-ink">Note for {e.patientName}</p>
                            <p className="text-xs text-slate-500">
                              Date: {new Date(e.date).toLocaleDateString()} • Status: {e.status}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <IconButton icon={<MessageCircle size={16} />} onClick={openSentinel} ariaLabel="Open Sentinel (Ctrl/Cmd+K)" />
        <div className="relative">
          <IconButton icon={<Inbox size={16} />} onClick={() => navigate('/messages')} ariaLabel="Open inbox" />
          {inboxUnread > 0 ? (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-ink text-white text-[10px] font-semibold flex items-center justify-center">
              {inboxUnread > 99 ? '99+' : inboxUnread}
            </span>
          ) : null}
        </div>
        <IconButton
          icon={theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          onClick={onToggleTheme}
          ariaLabel="Toggle theme"
        />
        <div className="relative" ref={notificationsRef}>
          <button
            className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-ink hover:shadow-sm transition relative"
            onClick={() => {
              setShowNotifications((prev) => !prev);
              if (!showNotifications && unreadCount > 0) {
                markAllRead();
              }
            }}
            aria-label="Notifications"
          >
            {unreadCount > 0 ? <BellDot size={18} /> : <Bell size={18} />}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-ink">{t('topbar.notifications', 'Notifications')}</p>
                  <p className="text-xs text-slate-500">{notifications.length} total</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={addMockEvent}
                    className="px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-700 text-xs hover:bg-slate-100"
                  >
                    {t('topbar.mock', 'Mock')}
                  </button>
                  <button
                    onClick={markAllRead}
                    className="p-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                    title={t('topbar.markAllRead', 'Mark all as read')}
                  >
                    <CheckCheck size={14} />
                  </button>
                  <button
                    onClick={clearAll}
                    className="p-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                    title={t('topbar.clearAll', 'Clear all')}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
              <div className="max-h-[430px] overflow-y-auto divide-y divide-slate-100">
                {!notifications.length ? (
                  <div className="p-4 text-sm text-slate-500">{t('topbar.noNotifications', 'No notifications yet.')}</div>
                ) : (
                  notifications.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        markRead(item.id);
                        if (item.href) {
                          navigate(item.href);
                          setShowNotifications(false);
                        }
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${
                        item.read ? 'bg-white' : 'bg-sky-50/50'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <Circle size={10} className={`mt-1 ${severityDotClass(item)}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-ink truncate">{tr(item.title)}</p>
                            <span className="text-[10px] uppercase tracking-wide text-slate-400 shrink-0">
                              {translateNotificationSource(item.source, tr)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5">{tr(item.message)}</p>
                          <p className="text-[11px] text-slate-400 mt-1">{formatRelativeTime(item.createdAt, language)}</p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <div
          className="relative"
          ref={helpRef}
          onMouseEnter={() => setShowHelp(true)}
          onMouseLeave={() => {
            // small delay to let users move into the panel
            setTimeout(() => {
              if (helpRef.current && !helpRef.current.matches(':hover')) {
                setShowHelp(false);
              }
            }, 120);
          }}
        >
          <IconButton icon={<Grid size={18} />} ariaLabel="Modules and help" />
          {showHelp && (
            <div className="absolute right-0 top-full mt-2 w-[640px] bg-white rounded-2xl shadow-2xl border border-slate-100 p-5 z-50 animate-in fade-in zoom-in-95 duration-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {helpSections.map((section) => (
                  <div key={section.title} className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{section.title}</p>
                    <div className="space-y-2">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const content = (
                          <>
                            <div className="h-9 w-9 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center">
                              <Icon size={16} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-ink">{item.title}</p>
                              <p className="text-xs text-slate-500">{item.desc}</p>
                            </div>
                          </>
                        );
                        if (item.href) {
                          return (
                            <Link
                              key={item.title}
                              to={item.href}
                              className="flex items-start gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors"
                              onClick={() => setShowHelp(false)}
                            >
                              {content}
                            </Link>
                          );
                        }
                        return (
                          <div key={item.title} className="flex items-start gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors">
                            {content}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <button
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 hover:bg-slate-100 transition"
            onClick={() => setShowMenu(!showMenu)}
          >
            <div className="h-8 w-8 rounded-full bg-ink text-white flex items-center justify-center text-sm font-semibold">
              {user?.name?.slice(0, 2).toUpperCase() || 'AD'}
            </div>
            <div className="leading-tight hidden md:block text-left">
              <p className="font-semibold text-sm text-ink">{user?.name ?? 'User'}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50 animate-in fade-in zoom-in-95 duration-200">
              <div className="px-4 py-3 border-b border-slate-50 mb-1">
                <p className="font-semibold text-sm text-ink">{user?.name ?? 'User'}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
              <button
                className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-ink flex items-center gap-2"
                onClick={() => {
                  setShowMenu(false);
                  navigate('/settings/account');
                }}
              >
                <User size={16} /> {t('topbar.profileSettings', 'Profile Settings')}
              </button>
              <div className="h-px bg-slate-50 my-1" />
              <div className="px-4 py-2">
                <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <Building2 size={13} /> {t('Connected accounts')}
                </p>
                {connectedAccounts.length ? connectedAccounts.map((account) => {
                  const isCurrent = account.user?.email === user?.email;
                  return (
                    <button
                      key={account.user?.email}
                      className={`w-full rounded-lg px-2 py-1.5 text-left text-xs ${isCurrent ? 'bg-slate-100 text-ink' : 'text-slate-600 hover:bg-slate-50'}`}
                      type="button"
                      disabled={isCurrent}
                      onClick={() => switchAccount(account)}
                    >
                      <span className="block font-medium">{account.user?.name || account.user?.email}</span>
                      <span className="block truncate text-slate-400">{account.user?.email}{isCurrent ? ` · ${t('Current')}` : ''}</span>
                    </button>
                  );
                }) : (
                  <p className="text-xs text-slate-400">{t('Sign in with another remembered account to switch here.')}</p>
                )}
              </div>
              <div className="h-px bg-slate-50 my-1" />
              <button
                className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                onClick={() =>
                  logout({
                    logoutParams: { returnTo: window.location.origin },
                  })
                }
              >
                <LogOut size={16} /> {t('topbar.signOut', 'Sign out')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

type IconButtonProps = { icon: ReactNode; onClick?: () => void; ariaLabel?: string };

function IconButton({ icon, onClick, ariaLabel }: IconButtonProps) {
  return (
    <button
      className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:text-ink hover:shadow-sm transition"
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {icon}
    </button>
  );
}

function severityDotClass(item: NotificationItem) {
  if (item.severity === 'critical') return 'text-rose-600 fill-rose-600';
  if (item.severity === 'warning') return 'text-amber-500 fill-amber-500';
  if (item.severity === 'success') return 'text-emerald-600 fill-emerald-600';
  return 'text-sky-500 fill-sky-500';
}

function translateNotificationSource(source: NotificationItem['source'], tr: (value: string) => string) {
  if (source === 'appointment') return tr('Appointment');
  if (source === 'patient') return tr('Patient');
  if (source === 'prescription') return tr('Prescription');
  if (source === 'billing') return tr('Billing');
  if (source === 'documents') return tr('Documents');
  if (source === 'telemed') return tr('Telemedicine');
  return tr('System');
}

function formatRelativeTime(value: string, language: string) {
  const ts = new Date(value).getTime();
  const locale = language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US';
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (Number.isNaN(ts)) return formatter.format(0, 'second');
  const diffMs = Date.now() - ts;
  const diffSec = Math.max(1, Math.floor(diffMs / 1000));
  if (diffSec < 60) return formatter.format(-diffSec, 'second');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return formatter.format(-diffMin, 'minute');
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return formatter.format(-diffHr, 'hour');
  const diffDay = Math.floor(diffHr / 24);
  return formatter.format(-diffDay, 'day');
}
